/**
 * OfferAccept Page - Team Offer Acceptance Flow
 *
 * Handles the Step 2 offer acceptance process where users accept or decline
 * team invitations after registration.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AdminPageLayout,
  PageHeader,
  Card,
  Button,
  LoadingState,
  Section
} from '../components';
import { AttributeStack, AttributeGrid } from '../components/admin/patterns/AttributePatterns';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import apiService from '../services/api';
import { CheckCircle, XCircle, Users, Calendar, User, Mail, Clock } from 'lucide-react';

const OfferAccept = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { showSuccess, showError } = useToast();

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Missing acceptance token. Please check your email link or contact support.');
      setLoading(false);
      return;
    }

    // Check if token is just whitespace or empty after trimming
    if (token.trim() === '') {
      setError('Invalid acceptance token. Please check your email link or contact support.');
      setLoading(false);
      return;
    }

    // Wait for auth check to complete
    if (!authLoading) {
      if (!isAuthenticated) {
        // Redirect to login with return URL
        navigate(`/auth?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        return;
      }

      loadOfferDetails();
    }
  }, [token, isAuthenticated, authLoading, navigate]);

  const loadOfferDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Call the backend endpoint to get offer details
      const response = await apiService.request(`/api/offers/${token}/view`);
      setOffer(response);
    } catch (err) {
      console.error('Failed to load offer details:', err);
      setError(err.message || 'Failed to load offer details. The offer may be expired or invalid.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      setAccepting(true);
      await apiService.request(`/api/offers/${token}/accept`, {
        method: 'POST'
      });

      showSuccess('Offer accepted! You are now enrolled in the team.');

      // Redirect to dashboard or inbox after a short delay
      setTimeout(() => {
        navigate('/inbox');
      }, 1500);
    } catch (err) {
      console.error('Failed to accept offer:', err);
      showError(err.message || 'Failed to accept offer. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    if (!window.confirm('Are you sure you want to decline this offer?')) {
      return;
    }

    try {
      setDeclining(true);
      await apiService.request(`/api/offers/${token}/decline`, {
        method: 'POST'
      });

      showSuccess('Offer declined.');

      // Redirect to inbox after a short delay
      setTimeout(() => {
        navigate('/inbox');
      }, 1500);
    } catch (err) {
      console.error('Failed to decline offer:', err);
      showError(err.message || 'Failed to decline offer. Please try again.');
    } finally {
      setDeclining(false);
    }
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <AdminPageLayout maxWidth="900px">
        <LoadingState />
      </AdminPageLayout>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  // Show error state
  if (error) {
    return (
      <AdminPageLayout maxWidth="900px">
        <PageHeader
          title="TEAM INVITATION"
          subtitle="Accept or decline your team offer"
          style={{ marginBottom: '2rem' }}
        />
        <Card level={1}>
          <div style={{
            padding: '3rem 2rem',
            textAlign: 'center'
          }}>
            <XCircle
              size={64}
              style={{
                color: '#dc2626',
                marginBottom: '1rem'
              }}
            />
            <h2 style={{
              color: '#dc2626',
              marginBottom: '1rem'
            }}>
              Unable to Load Offer
            </h2>
            <p style={{
              color: 'var(--color-text-secondary)',
              marginBottom: '2rem',
              maxWidth: '500px',
              margin: '0 auto 2rem'
            }}>
              {error}
            </p>
            <Button
              variant="primary"
              onClick={() => navigate('/inbox')}
            >
              GO TO INBOX
            </Button>
          </div>
        </Card>
      </AdminPageLayout>
    );
  }

  // Show loading state while fetching offer
  if (loading) {
    return (
      <AdminPageLayout maxWidth="900px">
        <PageHeader
          title="TEAM INVITATION"
          subtitle="Accept or decline your team offer"
          style={{ marginBottom: '2rem' }}
        />
        <LoadingState />
      </AdminPageLayout>
    );
  }

  // Show offer details
  return (
    <AdminPageLayout maxWidth="900px">
      <PageHeader
        title="TEAM INVITATION"
        subtitle="Review and respond to your team offer"
        style={{ marginBottom: '2rem' }}
      />

      <Section
        title="OFFER DETAILS"
        description="You've been invited to join a team"
      >
        <Card level={1}>
          <div style={{ padding: '2rem' }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '2rem',
              paddingBottom: '2rem',
              borderBottom: '1px solid var(--color-border)'
            }}>
              <CheckCircle
                size={64}
                style={{
                  color: 'var(--color-primary)',
                  marginBottom: '1rem'
                }}
              />
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: 700,
                color: 'var(--color-primary)',
                marginBottom: '0.5rem'
              }}>
                {offer?.team?.name || 'Team'}
              </h2>
              <p style={{
                color: 'var(--color-text-secondary)',
                fontSize: '1.1rem'
              }}>
                {offer?.club?.name || 'Club'}
              </p>
            </div>

            <AttributeGrid columns="2" gap="2rem">
              <AttributeStack
                label="Player Name"
                value={offer?.player_name || 'N/A'}
                icon={<User size={16} />}
              />
              <AttributeStack
                label="Parent Name"
                value={offer?.parent_name || 'N/A'}
                icon={<User size={16} />}
              />
              {offer?.team?.age_group && (
                <AttributeStack
                  label="Age Group"
                  value={offer.team.age_group}
                  icon={<Users size={16} />}
                />
              )}
              {offer?.team?.season && (
                <AttributeStack
                  label="Season"
                  value={offer.team.season}
                  icon={<Calendar size={16} />}
                />
              )}
              {offer?.team?.coach_info?.name && (
                <AttributeStack
                  label="Coach"
                  value={offer.team.coach_info.name}
                  icon={<User size={16} />}
                />
              )}
              {offer?.expires_at && (
                <AttributeStack
                  label="Offer Expires"
                  value={new Date(offer.expires_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                  icon={<Clock size={16} />}
                />
              )}
            </AttributeGrid>

            {offer?.team?.description && (
              <div style={{
                marginTop: '2rem',
                padding: '1.5rem',
                background: 'var(--color-background-elevated)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)'
              }}>
                <h3 style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '0.5rem'
                }}>
                  Team Description
                </h3>
                <p style={{
                  color: 'var(--color-text-primary)',
                  lineHeight: 1.6,
                  margin: 0
                }}>
                  {offer.team.description}
                </p>
              </div>
            )}

            {offer?.team?.roster_size !== undefined && offer?.team?.max_roster_size && (
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem 1.5rem',
                background: offer.team.roster_size >= offer.team.max_roster_size
                  ? 'rgba(239, 68, 68, 0.1)'
                  : 'rgba(34, 197, 94, 0.1)',
                borderRadius: 'var(--radius-md)',
                border: `1px solid ${offer.team.roster_size >= offer.team.max_roster_size
                  ? 'rgba(239, 68, 68, 0.3)'
                  : 'rgba(34, 197, 94, 0.3)'}`
              }}>
                <p style={{
                  color: 'var(--color-text-primary)',
                  fontWeight: 500,
                  margin: 0
                }}>
                  <Users size={16} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  Team Roster: {offer.team.roster_size} / {offer.team.max_roster_size} players
                </p>
              </div>
            )}

            <div style={{
              marginTop: '2.5rem',
              paddingTop: '2rem',
              borderTop: '1px solid var(--color-border)',
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Button
                variant="primary"
                onClick={handleAccept}
                disabled={accepting || declining}
                style={{ minWidth: '180px' }}
              >
                {accepting ? 'ACCEPTING...' : 'ACCEPT & JOIN TEAM'}
              </Button>
              <Button
                variant="secondary"
                onClick={handleDecline}
                disabled={accepting || declining}
                style={{ minWidth: '180px' }}
              >
                {declining ? 'DECLINING...' : 'DECLINE OFFER'}
              </Button>
            </div>
          </div>
        </Card>
      </Section>
    </AdminPageLayout>
  );
};

export default OfferAccept;
